import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { schemePalette } from '@if/mui'
import { Button, styled, Typography } from '@mui/material'
import { PickersDayProps } from '@mui/x-date-pickers'
import { HTMLProps, memo, SyntheticEvent, useCallback, useMemo } from 'react'

export const DAY_WIDTH_PX = 80

export interface CatchUpAmountOfOverdueForDateDaySlotProps {
  lateAmountForDays: Map<string, number> | undefined
}

const DayButton = styled(Button)(({ disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: DAY_WIDTH_PX,
  cursor: disabled ? 'default' : 'pointer',
}))

interface DayCaptionProps {
  disabled?: boolean
  selected?: boolean
  today?: boolean
  showDaysOutsideCurrentMonth?: boolean
  outsideCurrentMonth?: boolean
}

const DayCaptionPropsSet = new Set<keyof DayCaptionProps>([
  'disabled',
  'selected',
  'today',
  'showDaysOutsideCurrentMonth',
  'outsideCurrentMonth',
])
const DayCaption = styled(Typography, {
  shouldForwardProp: (prop) => !DayCaptionPropsSet.has(prop as keyof DayCaptionProps),
})<DayCaptionProps>(({ theme, disabled, selected, today, showDaysOutsideCurrentMonth, outsideCurrentMonth }) => {
  const palette = schemePalette(theme)
  return {
    color: disabled
      ? palette.action.disabled
      : selected
        ? palette.primary.contrastText
        : showDaysOutsideCurrentMonth && outsideCurrentMonth
          ? palette.text.secondary
          : palette.text.primary,
    borderRadius: '50%',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: today && !selected ? `1px solid ${disabled ? palette.action.disabled : palette.text.primary}` : 'none',
    backgroundColor: selected ? palette.primary.main : 'transparent',
    transition: theme.transitions.create(['background-color'], { duration: theme.transitions.duration.short }),
  }
})

interface AmountCaptionProps {
  disabled?: boolean
  selected?: boolean
}
const AmountCaption = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'selected',
})<AmountCaptionProps>(({ theme, disabled, selected }) => {
  const palette = schemePalette(theme)
  return {
    color: disabled ? palette.action.disabled : selected ? palette.primary.main : palette.text.secondary,
  }
})

const CatchUpAmountOfOverdueForDateDaySlot = ({
  lateAmountForDays,
  color,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseEnter,
  day,
  outsideCurrentMonth,
  disabled,
  selected,
  onDaySelect,
  today,
  showDaysOutsideCurrentMonth,

  // Unused props from PickersDayProps since the parent is not using them we are not handling them
  disableHighlightToday,
  isAnimating,
  isFirstVisibleCell,
  isLastVisibleCell,
  ...props
}: CatchUpAmountOfOverdueForDateDaySlotProps & PickersDayProps) => {
  const lateAmount = lateAmountForDays?.get(DateOnly.fromDate(day).toString()) || 0

  const handleClick = useCallback(() => {
    if (!disabled) {
      onDaySelect(day)
    }
  }, [day, disabled, onDaySelect])

  // Generate event handlers that include the day information witch is needed by the parent component
  const events = useMemo(() => {
    const handlerWrapper = <E extends SyntheticEvent<HTMLButtonElement>>(handler?: (event: E, day: Date) => void) => {
      if (handler) {
        return (event: SyntheticEvent<HTMLButtonElement>) => handler(event as E, day)
      }
    }
    const map: Record<
      keyof Pick<HTMLProps<HTMLButtonElement>, 'onFocus' | 'onBlur' | 'onKeyDown' | 'onMouseEnter'>,
      ((event: SyntheticEvent<HTMLButtonElement>) => void) | undefined
    > = {
      onFocus: handlerWrapper(onFocus),
      onBlur: handlerWrapper(onBlur),
      onKeyDown: handlerWrapper(onKeyDown),
      onMouseEnter: handlerWrapper(onMouseEnter),
    }
    return map
  }, [day, onBlur, onFocus, onKeyDown, onMouseEnter])

  // If the day is outside the current month, PickersDay will render a filler day.
  if (outsideCurrentMonth && !showDaysOutsideCurrentMonth) return <DayButton key={day.toString()} disabled {...props} />

  return (
    <DayButton
      key={day.toString()}
      onClick={handleClick}
      disabled={disabled}
      color={selected ? 'primary' : 'inherit'}
      {...events}
      {...props}
    >
      <DayCaption
        variant="caption"
        selected={selected}
        disabled={disabled}
        today={today}
        outsideCurrentMonth={outsideCurrentMonth}
        showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
      >
        {day.getDate()}
      </DayCaption>
      <AmountCaption variant="caption" disabled={disabled} selected={selected}>
        {lateAmount ? formatCurrency(lateAmount) : '-'}
      </AmountCaption>
    </DayButton>
  )
}

export default memo(CatchUpAmountOfOverdueForDateDaySlot)
